import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ActivityMetricsTable from './components/ActivityMetricsTable';
import { ActivityMetricsTableData } from './components/ActivityMetricsTable/data';
import { ActivityMetricsFilters } from './components/ActivityMetricsFilters';
import api from 'api';
import usePageParams from 'hooks/usePageParams';
import useQueryParams from 'hooks/useQueryParams';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useActivityMetricsMultiSort from './hooks/useActivityMetricsMultiSort';
import {
  HAS_LISTING_FILTER_KEY,
  LISTING_STATUS_FILTER_KEY,
} from './components/ActivityMetricsFilters/ActivityMetricsFilters.component';
import MultiSort from 'components/MultiSort';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';

import './ActivityMetrics.styles.scss';

type ActivityMetricsProps = {
  className?: string;
};

const ActivityMetrics: React.FC<ActivityMetricsProps> = (props) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [activityMetrics, setActivityMetrics] = useState<
    ActivityMetricsTableData[]
  >([]);

  const classes = classNames('activity-metrics', className);

  const { t } = useTranslation();

  const page = usePageParams();

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const { params } = useQueryParams();

  const sort = params?.sort;
  const hasListing = params?.[HAS_LISTING_FILTER_KEY];
  const listingStatus = params?.[LISTING_STATUS_FILTER_KEY];

  const { multiSortValues, multiSortDirectionTranslation } =
    useActivityMetricsMultiSort();

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await api.portfolio.admin.getActivityMetrics({
          $page: page,
          $sort: sort,
          $limit: limit,
          ...(hasListing && { hasListing }),
          ...(listingStatus && { listingStatus }),
        });
        setActivityMetrics(items);
        setTotalPages(totalPages);
      } catch {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [limit, page, sort, hasListing, listingStatus]);

  return (
    <div className={classes}>
      <div className="activity-metrics__heading">
        <h1>{t('dealMetrics')}</h1>

        <div className="activity-metrics__heading__right">
          <NumberOfResultsPerPageFilter
            limit={limit}
            updateLimit={updateLimit}
          />
          <ActivityMetricsFilters />
          <MultiSort
            multiSortValues={multiSortValues}
            multiSortDirectionTranslation={multiSortDirectionTranslation}
          />
        </div>
      </div>

      <ActivityMetricsTable
        isLoading={isLoading}
        totalPages={totalPages}
        data={activityMetrics}
      />
    </div>
  );
};

export default ActivityMetrics;
